import React, { ReactElement, useContext } from 'react';

import { Box, Typography } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import LockIcon from '../../assets/icons/LockIcon';
import { TenantCtx } from '../../containers/CommonLayout/CommonLayout';
import styled from '../../styled';
import {
  colorsPalette,
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_BIG_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
  useIsWidthUp,
} from '../../theme';
import { TenantsRefs } from '../../utils/const';
import AnimatedSkeleton from '../AnimatedSkeleton';
import AnimatedSkeletonMultiline from '../AnimatedSkeletonMultiline';
import { CustomButton } from '../UI/CustomButton';
import useStyles from './styles';

interface JoinNowProps {
  className?: string;
  verticalSkeleton?: boolean;
  isLoggedIn?: boolean;
  title?: string;
  description?: string | JSX.Element;
  btnCaption?: string;
  classes?: {
    lockText?: string;
    description?: string;
    lockIcon?: string;
    link?: string;
  };
  Icon?: (props: any) => ReactElement;
  loading?: boolean;
  skeletonLines?: number;
}

const JoinNowBlock: React.FC<JoinNowProps> = ({
  className,
  loading,
  verticalSkeleton,
  isLoggedIn,
  classes,
  title,
  description,
  btnCaption,
  skeletonLines = 3,
  Icon = LockIcon,
  ...rest
}) => {
  const defaultClasses = useStyles();
  const { tenant } = useContext(TenantCtx);
  const { t } = useTranslation();
  const isEmTenant = tenant?.name === TenantsRefs.Em;
  const isMobile = !useIsWidthUp('md');

  if (loading) {
    return (
      <LoadingWrapper className={className} data-testid={rest['data-testid']}>
        {verticalSkeleton ? (
          <LoadingContent>
            <LoadingTitle
              xs={SKELETON_TEXT_HEIGHT_PX}
              lines={2}
              spacer={TINY_MARGIN_PX}
              lastLineWidth={70}
            />
            <LoadingDescription
              xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
              lines={5}
              spacer={TINY_MARGIN_PX}
            />
            <AnimatedSkeleton xs={SKELETON_TEXT_HEIGHT_BIG_PX} />
          </LoadingContent>
        ) : (
          <>
            {!isMobile && (
              <LoadingHorizontalTitle
                xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                lines={2}
                spacer={TINY_MARGIN_PX}
                lastLineWidth={100}
              />
            )}
            <LoadingHorizontalDescription
              xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
              lines={skeletonLines}
              spacer={TINY_MARGIN_PX}
            />
            <LoadingButton xs={SKELETON_TEXT_HEIGHT_BIG_PX} />
          </>
        )}
      </LoadingWrapper>
    );
  }

  return (
    <Wrapper className={className} data-testid={rest['data-testid']}>
      <Icon className={cx(defaultClasses.lockIcon, classes?.lockIcon)} />
      <Typography variant="h2" className={cx(defaultClasses.lockText, classes?.lockText)}>
        {title ||
          t(
            isLoggedIn
              ? 'page.video.subscribeToWatch'
              : isEmTenant
              ? 'page.video.bookADemoToAccess'
              : 'page.video.joinToWatch',
          )}
      </Typography>
      {typeof description === 'string' || typeof description === 'undefined' ? (
        <Typography className={cx(defaultClasses.description, classes?.description)}>
          {description ||
            (isLoggedIn
              ? t('page.video.accessFreeTrial')
              : isEmTenant
              ? t('page.video.bookToAccessOtherVideos')
              : t('page.video.accessFreeTrial'))}
        </Typography>
      ) : (
        description
      )}
      <Link
        to={isLoggedIn ? '/pricing' : isEmTenant ? '/contact' : '/pricing'}
        data-testid="joinNowLink"
        className={cx(defaultClasses.link, classes?.link)}
      >
        <CustomButton>
          {btnCaption ||
            (isLoggedIn
              ? t('button.startFreeTrial')
              : isEmTenant
              ? t('button.bookADemo')
              : t('button.startFreeTrial'))}
        </CustomButton>
      </Link>
    </Wrapper>
  );
};

export default JoinNowBlock;

const LoadingTitle = styled(AnimatedSkeletonMultiline)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const LoadingHorizontalTitle = styled(AnimatedSkeletonMultiline)`
  flex-grow: 1;
  width: 100%;
  margin: auto 0 ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: unset;
    margin: auto 0;
  }
`;
const LoadingContent = styled('div')`
  width: 100%;
`;
const LoadingDescription = styled(AnimatedSkeletonMultiline)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const LoadingHorizontalDescription = styled(AnimatedSkeletonMultiline)`
  flex-grow: 5;
  margin-bottom: ${SMALL_MARGIN_PX};
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: 0 ${MIDDLE_MARGIN_PX};
    width: unset;
  }
`;
const Wrapper = styled(Box)`
  border-radius: 10px;
  padding: 24px 24px 24px 72px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  background: ${colorsPalette.violetAnalyticsCard};
  position: relative;
  align-items: flex-start;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    padding: 32px ${MIDDLE_MARGIN_PX};
    align-items: center;
  }
`;
const LoadingButton = styled(AnimatedSkeleton)`
  width: 100%;
  flex-grow: 1;
  min-width: 200px;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: unset;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: unset;
  }
`;
const LoadingWrapper = styled(Wrapper)`
  padding-left: ${SUBMIDDLE_MARGIN_PX};
`;
